import React, { useState } from "react";

const S = {
  wrapper: {
    marginTop: '10px', marginLeft: '0px', border: '3px', padding: '40px',
    borderStyle: 'solid', borderColor: 'grey', fontFamily: "'Georgia', serif",
    color: '#1a1a2e', lineHeight: '1.7',
  },
  h1: { fontSize: '2rem', fontWeight: '700', borderBottom: '3px solid #2E75B6', paddingBottom: '8px', marginBottom: '24px', color: '#1F3864' },
  h2: { fontSize: '1.3rem', fontWeight: '700', color: '#2E75B6', marginTop: '36px', marginBottom: '12px', borderLeft: '4px solid #2E75B6', paddingLeft: '10px' },
  p: { marginBottom: '12px', fontSize: '0.97rem' },
  infoBox: { background: '#EBF3FB', border: '2px solid #2E75B6', borderRadius: '6px', padding: '16px 20px', margin: '16px 0' },
  infoBoxTitle: { fontWeight: '700', fontFamily: "'Courier New', monospace", fontSize: '0.88rem', color: '#1F3864', marginBottom: '8px' },
  infoBoxItem: { fontSize: '0.9rem', margin: '4px 0', paddingLeft: '12px', borderLeft: '2px solid #2E75B6' },
  table: { width: '100%', borderCollapse: 'collapse', marginBottom: '20px', fontSize: '0.92rem' },
  th: { background: '#2E75B6', color: '#fff', padding: '10px 14px', textAlign: 'left', fontWeight: '700', fontSize: '0.9rem' },
  tdE: { padding: '10px 14px', borderBottom: '1px solid #ccd9ea', verticalAlign: 'top', background: '#f5f9ff' },
  tdO: { padding: '10px 14px', borderBottom: '1px solid #ccd9ea', verticalAlign: 'top', background: '#fff' },
  tdL: { fontWeight: '600', color: '#1F3864', whiteSpace: 'nowrap', width: '220px' },
  code: { fontFamily: "'Courier New', monospace", fontSize: '0.88rem', whiteSpace: 'pre-wrap' },
  dWrap: { margin: '24px 0', background: '#f8fbff', border: '1px solid #ccd9ea', borderRadius: '8px', padding: '20px' },
  dTitle: { fontWeight: '700', fontSize: '0.95rem', color: '#1F3864', marginBottom: '12px', textAlign: 'center', letterSpacing: '0.02em' },
  dCaption: { fontSize: '0.84rem', color: '#555', textAlign: 'center', marginTop: '10px', fontStyle: 'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width: '220px' }}>Operation</th>
        <th style={S.th}>Definition / Notes</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i % 2 === 0 ? S.tdE : S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i % 2 === 0 ? S.tdE : S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 1 — The Number Line
// ─────────────────────────────────────────────────────────────────
function DiagramNumberLine() {
  const W = 520, H = 220;
  const ox = 260, lineY = 80, sc = 40;
  const toX = v => ox + v * sc;

  // absolute value: distance between -3 and 2
  const a = -3, b = 2;
  const dist = Math.abs(b - a);

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>The Number Line — Integers, Sign, and Absolute Value as Distance</div>
      <div style={{ textAlign: 'center' }}>
        <svg width={W} height={H} viewBox={"0 0 " + W + " " + H} style={{ maxWidth: '100%' }}>
          <defs>
            <marker id="nlArr" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto"><polygon points="0 0,7 3.5,0 7" fill="#1F3864"/></marker>
            <marker id="nlArrL" markerWidth="7" markerHeight="7" refX="1" refY="3.5" orient="auto"><polygon points="7 0,0 3.5,7 7" fill="#1F3864"/></marker>
          </defs>

          {/* Main number line */}
          <line x1={20} y1={lineY} x2={W-20} y2={lineY} stroke="#1F3864" strokeWidth="2" markerEnd="url(#nlArr)" markerStart="url(#nlArrL)"/>

          {/* Ticks and labels */}
          {[-6,-5,-4,-3,-2,-1,0,1,2,3,4,5,6].map(v => (
            <g key={v}>
              <line x1={toX(v)} y1={lineY-7} x2={toX(v)} y2={lineY+7} stroke="#1F3864" strokeWidth={v===0?2.5:1.5}/>
              <text x={toX(v)} y={lineY+20} textAnchor="middle" fontSize="12" fill={v===0?'#1F3864':'#555'} fontWeight={v===0?'700':'400'}>{v}</text>
            </g>
          ))}

          {/* Negative region shade */}
          <rect x={toX(-6)} y={lineY-5} width={toX(0)-toX(-6)} height={10} fill="#ffd0d0" opacity="0.4" rx={2}/>
          <rect x={toX(0)} y={lineY-5} width={toX(6)-toX(0)} height={10} fill="#d0e8ff" opacity="0.4" rx={2}/>
          <text x={toX(-3)} y={lineY-14} textAnchor="middle" fontSize="11" fill="#c0392b" fontWeight="700">negative</text>
          <text x={toX(3)}  y={lineY-14} textAnchor="middle" fontSize="11" fill="#2E75B6" fontWeight="700">positive</text>

          {/* Distance between a and b */}
          <circle cx={toX(a)} cy={lineY} r={6} fill="#c0392b" stroke="#fff" strokeWidth="1.5"/>
          <circle cx={toX(b)} cy={lineY} r={6} fill="#27ae60" stroke="#fff" strokeWidth="1.5"/>
          <text x={toX(a)} y={lineY-22} textAnchor="middle" fontSize="11" fill="#c0392b" fontWeight="700">a = {a}</text>
          <text x={toX(b)} y={lineY-22} textAnchor="middle" fontSize="11" fill="#27ae60" fontWeight="700">b = {b}</text>

          {/* Distance bracket */}
          <line x1={toX(a)} y1={lineY+38} x2={toX(b)} y2={lineY+38} stroke="#e07b00" strokeWidth="2"/>
          <line x1={toX(a)} y1={lineY+32} x2={toX(a)} y2={lineY+44} stroke="#e07b00" strokeWidth="2"/>
          <line x1={toX(b)} y1={lineY+32} x2={toX(b)} y2={lineY+44} stroke="#e07b00" strokeWidth="2"/>
          <text x={(toX(a)+toX(b))/2} y={lineY+58} textAnchor="middle" fontSize="12" fill="#e07b00" fontWeight="700">|b - a| = |{b}-({a})| = {dist}</text>

          {/* Absolute value boxes */}
          <rect x={30} y={H-58} width={205} height={48} rx={4} fill="#fff" stroke="#ccd9ea"/>
          <text x={132} y={H-42} textAnchor="middle" fontSize="11" fill="#1F3864" fontWeight="700">Absolute Value = distance from 0</text>
          <text x={132} y={H-26} textAnchor="middle" fontSize="11" fill="#c0392b" fontFamily="'Courier New',monospace">|-5| = 5      |+5| = 5</text>
          <text x={132} y={H-12} textAnchor="middle" fontSize="10" fill="#888">magnitude, ignoring sign</text>

          <rect x={W-225} y={H-58} width={195} height={48} rx={4} fill="#EBF3FB" stroke="#2E75B6"/>
          <text x={W-128} y={H-42} textAnchor="middle" fontSize="11" fill="#1F3864" fontWeight="700">Distance between two points</text>
          <text x={W-128} y={H-26} textAnchor="middle" fontSize="11" fill="#e07b00" fontFamily="'Courier New',monospace">d = |b - a| = |a - b|</text>
          <text x={W-128} y={H-12} textAnchor="middle" fontSize="10" fill="#888">always positive regardless of order</text>
        </svg>
      </div>
      <div style={S.dCaption}>
        The number line puts every real number in order. Negative numbers lie left of zero, positive numbers right. Absolute value strips the sign — it is purely the distance from zero, always non-negative.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 2 — The Four Basic Operations Visualized
// ─────────────────────────────────────────────────────────────────
function DiagramOperations() {
  const W = 520, H = 260;
  const lineY = 70, sc = 34, ox = 180;
  const toX = v => ox + v * sc;

  // Addition: 3 + 4 = 7
  // Subtraction: 7 - 4 = 3
  // Multiplication: 3 x 4 = 12 (3 groups of 4)
  // Division: 12 / 4 = 3 (split 12 into 4 equal groups)

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>The Four Operations — Visualized on a Number Line and Area Model</div>
      <div style={{ textAlign: 'center' }}>
        <svg width={W} height={H} viewBox={"0 0 " + W + " " + H} style={{ maxWidth: '100%' }}>
          <defs>
            <marker id="opArr" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto"><polygon points="0 0,7 3.5,0 7" fill="#2E75B6"/></marker>
            <marker id="opArrR" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto"><polygon points="0 0,7 3.5,0 7" fill="#c0392b"/></marker>
            <marker id="opArrG" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto"><polygon points="0 0,7 3.5,0 7" fill="#27ae60"/></marker>
            <marker id="opArrGL" markerWidth="7" markerHeight="7" refX="1" refY="3.5" orient="auto"><polygon points="7 0,0 3.5,7 7" fill="#27ae60"/></marker>
          </defs>

          {/* ── TOP ROW: Addition and Subtraction on number lines ── */}
          {/* Addition number line */}
          <line x1={20} y1={lineY} x2={310} y2={lineY} stroke="#ddd" strokeWidth="1.5"/>
          {[0,1,2,3,4,5,6,7,8].map(v => (
            <g key={v}>
              <line x1={toX(v)} y1={lineY-4} x2={toX(v)} y2={lineY+4} stroke="#bbb" strokeWidth="1"/>
              <text x={toX(v)} y={lineY+15} textAnchor="middle" fontSize="10" fill="#bbb">{v}</text>
            </g>
          ))}
          {/* +3 arrow */}
          <path d={"M "+toX(0)+","+(lineY-10)+" Q "+toX(1.5)+","+(lineY-30)+" "+toX(3)+","+(lineY-10)} fill="none" stroke="#2E75B6" strokeWidth="2" markerEnd="url(#opArr)"/>
          <text x={toX(1.5)} y={lineY-32} textAnchor="middle" fontSize="11" fill="#2E75B6" fontWeight="700">+3</text>
          {/* +4 arrow */}
          <path d={"M "+toX(3)+","+(lineY-10)+" Q "+toX(5)+","+(lineY-30)+" "+toX(7)+","+(lineY-10)} fill="none" stroke="#27ae60" strokeWidth="2" markerEnd="url(#opArrG)"/>
          <text x={toX(5)} y={lineY-32} textAnchor="middle" fontSize="11" fill="#27ae60" fontWeight="700">+4</text>
          <circle cx={toX(0)} cy={lineY} r={4} fill="#1F3864"/>
          <circle cx={toX(3)} cy={lineY} r={4} fill="#2E75B6"/>
          <circle cx={toX(7)} cy={lineY} r={5} fill="#e07b00" stroke="#fff" strokeWidth="1.5"/>
          <text x={toX(7)+6} y={lineY+4} fontSize="12" fill="#e07b00" fontWeight="700">= 7</text>
          <text x={toX(3.5)} y={lineY+30} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">3 + 4 = 7</text>
          <text x={20} y={lineY-42} fontSize="11" fill="#1F3864" fontWeight="700">Addition — move right</text>

          {/* Subtraction number line */}
          <line x1={W-310} y1={lineY+80} x2={W-20} y2={lineY+80} stroke="#ddd" strokeWidth="1.5"/>
          {[0,1,2,3,4,5,6,7,8].map(v => {
            const lx = (W-310) + v*sc*0.82;
            return (
              <g key={v}>
                <line x1={lx} y1={lineY+76} x2={lx} y2={lineY+84} stroke="#bbb" strokeWidth="1"/>
                <text x={lx} y={lineY+95} textAnchor="middle" fontSize="10" fill="#bbb">{v}</text>
              </g>
            );
          })}
          {(() => {
            const sc2 = sc*0.82, ox2 = W-310;
            const tX2 = v => ox2 + v*sc2;
            return (
              <g>
                <path d={"M "+tX2(7)+","+(lineY+70)+" Q "+tX2(5)+","+(lineY+50)+" "+tX2(3)+","+(lineY+70)} fill="none" stroke="#c0392b" strokeWidth="2" markerEnd="url(#opArrR)"/>
                <text x={tX2(5)} y={lineY+48} textAnchor="middle" fontSize="11" fill="#c0392b" fontWeight="700">-4</text>
                <circle cx={tX2(7)} cy={lineY+80} r={4} fill="#1F3864"/>
                <circle cx={tX2(3)} cy={lineY+80} r={5} fill="#e07b00" stroke="#fff" strokeWidth="1.5"/>
                <text x={tX2(3)+6} y={lineY+84} fontSize="12" fill="#e07b00" fontWeight="700">= 3</text>
                <text x={tX2(4)} y={lineY+110} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">7 - 4 = 3</text>
                <text x={tX2(0)} y={lineY+38} fontSize="11" fill="#1F3864" fontWeight="700">Subtraction — move left</text>
              </g>
            );
          })()}

          {/* ── BOTTOM ROW: Multiplication (area) and Division (groups) ── */}
          {/* Multiplication area model */}
          const mx0 = 30, my0 = 155, mw = 120, mh = 60;
          <rect x={30} y={155} width={120} height={60} fill="#d0e8ff" stroke="#2E75B6" strokeWidth="2"/>
          {[1,2].map(i => <line key={i} x1={30+i*40} y1={155} x2={30+i*40} y2={215} stroke="#2E75B6" strokeWidth="1" strokeDasharray="3,2"/>)}
          <text x={90} y={190} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Area = 12</text>
          <text x={90} y={150} textAnchor="middle" fontSize="11" fill="#2E75B6" fontWeight="700">width = 4</text>
          <text x={22} y={190} textAnchor="end" fontSize="11" fill="#2E75B6" fontWeight="700">h=3</text>
          <text x={90} y={232} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">3 x 4 = 12</text>
          <text x={30} y={148} fontSize="11" fill="#1F3864" fontWeight="700">Multiplication — area model</text>

          {/* Division — splitting into equal groups */}
          {(() => {
            const gx = 200, gy = 155, gw = 36, gh = 56, gap = 10;
            return (
              <g>
                <text x={gx} y={gy-7} fontSize="11" fill="#1F3864" fontWeight="700">Division — split into equal groups</text>
                {[0,1,2,3].map(i => (
                  <g key={i}>
                    <rect x={gx+i*(gw+gap)} y={gy} width={gw} height={gh} fill="#fff3cd" stroke="#e07b00" strokeWidth="1.5" rx={3}/>
                    <text x={gx+i*(gw+gap)+gw/2} y={gy+gh/2+5} textAnchor="middle" fontSize="13" fill="#e07b00" fontWeight="700">3</text>
                  </g>
                ))}
                <text x={gx+2*(gw+gap)} y={gy+gh+18} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">12 / 4 = 3  (4 groups of 3)</text>
                {/* Inversion panel */}
                <rect x={gx+4*(gw+gap)+10} y={gy} width={130} height={gh} rx={4} fill="#f0fff4" stroke="#27ae60" strokeWidth="1.3"/>
                <text x={gx+4*(gw+gap)+75} y={gy+20} textAnchor="middle" fontSize="11" fill="#27ae60" fontWeight="700">Division = multiply</text>
                <text x={gx+4*(gw+gap)+75} y={gy+35} textAnchor="middle" fontSize="11" fill="#27ae60" fontWeight="700">by the inverse:</text>
                <text x={gx+4*(gw+gap)+75} y={gy+52} textAnchor="middle" fontSize="11" fill="#27ae60" fontFamily="'Courier New',monospace">12/4 = 12 * (1/4)</text>
              </g>
            );
          })()}
        </svg>
      </div>
      <div style={S.dCaption}>
        Addition moves right on the number line, subtraction moves left. Multiplication is repeated addition — visualized as the area of a rectangle. Division splits into equal groups, equivalent to multiplying by the reciprocal.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 3 — Squaring and Square Roots
// ─────────────────────────────────────────────────────────────────
function DiagramSquareRoot() {
  const W = 520, H = 270, ox = 80, oy = 200, sc = 52;
  const toX = x => ox + x * sc;
  const toY = y => oy - y * sc;

  // Draw y=x^2 and y=sqrt(x) and mirror line y=x
  const sqPts = [], sqrtPts = [], mirPts = [];
  for (let x = 0; x <= 3.5; x += 0.04) {
    sqPts.push(toX(x).toFixed(1) + ',' + toY(x*x).toFixed(1));
    if (x > 0) sqrtPts.push(toX(x).toFixed(1) + ',' + toY(Math.sqrt(x)).toFixed(1));
    mirPts.push(toX(x).toFixed(1) + ',' + toY(x).toFixed(1));
  }

  // perfect squares
  const perfSq = [[1,1],[2,4],[3,9]];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Squaring and Square Roots — Geometric Meaning and Inverse Relationship</div>
      <div style={{ textAlign: 'center' }}>
        <svg width={W} height={H} viewBox={"0 0 " + W + " " + H} style={{ maxWidth: '100%' }}>
          {/* Grid */}
          {[0,1,2,3,4,5,6,7,8,9].map(v => <line key={v} x1={toX(0)} y1={toY(v)} x2={toX(3.6)} y2={toY(v)} stroke="#eee" strokeWidth="1"/>)}
          {[0,1,2,3].map(v => <line key={v} x1={toX(v)} y1={toY(0)} x2={toX(v)} y2={toY(9)} stroke="#eee" strokeWidth="1"/>)}
          {/* Axes */}
          <line x1={ox-10} y1={oy} x2={toX(3.6)} y2={oy} stroke="#1F3864" strokeWidth="1.5"/>
          <line x1={ox}    y1={oy+10} x2={ox} y2={toY(9.5)} stroke="#1F3864" strokeWidth="1.5"/>
          <text x={toX(3.6)+4} y={oy+4} fontSize="11" fill="#1F3864" fontWeight="700">x</text>
          <text x={ox+4} y={toY(9.5)} fontSize="11" fill="#1F3864" fontWeight="700">y</text>
          {[1,2,3].map(v => <text key={v} x={toX(v)} y={oy+14} textAnchor="middle" fontSize="10" fill="#bbb">{v}</text>)}
          {[1,2,3,4,5,6,7,8,9].map(v => <text key={v} x={ox-6} y={toY(v)+3} textAnchor="end" fontSize="9" fill="#bbb">{v}</text>)}

          {/* Mirror line y=x */}
          <path d={'M '+mirPts.join(' L ')} fill="none" stroke="#ccc" strokeWidth="1.2" strokeDasharray="5,4"/>
          <text x={toX(2.1)+6} y={toY(2.1)-5} fontSize="10" fill="#bbb">y = x</text>

          {/* y = x^2 */}
          <path d={'M '+sqPts.join(' L ')} fill="none" stroke="#2E75B6" strokeWidth="2.4"/>
          <text x={toX(2.2)+6} y={toY(2.2*2.2)-6} fontSize="12" fill="#2E75B6" fontWeight="700">y = x²</text>

          {/* y = sqrt(x) */}
          <path d={'M '+sqrtPts.join(' L ')} fill="none" stroke="#c0392b" strokeWidth="2.4"/>
          <text x={toX(3.2)+6} y={toY(Math.sqrt(3.2))+4} fontSize="12" fill="#c0392b" fontWeight="700">y = sqrt(x)</text>

          {/* Perfect squares geometric boxes */}
          {perfSq.map(([side, area], i) => {
            const bx = toX(side-0.02), by = toY(area);
            return (
              <g key={i}>
                <circle cx={bx} cy={by} r={5} fill="#2E75B6" stroke="#fff" strokeWidth="1.5"/>
                <circle cx={toX(side-0.02)} cy={toY(Math.sqrt(area))} r={5} fill="#c0392b" stroke="#fff" strokeWidth="1.5"/>
              </g>
            );
          })}

          {/* Geometric square illustration */}
          <rect x={330} y={20} width={60} height={60} fill="#d0e8ff" stroke="#2E75B6" strokeWidth="2"/>
          <text x={360} y={55} textAnchor="middle" fontSize="13" fill="#2E75B6" fontWeight="700">Area</text>
          <text x={360} y={70} textAnchor="middle" fontSize="11" fill="#2E75B6">= 3²= 9</text>
          <text x={360} y={14} textAnchor="middle" fontSize="11" fill="#2E75B6" fontWeight="700">side = 3</text>
          <text x={316} y={55} textAnchor="end" fontSize="11" fill="#2E75B6">3</text>
          <text x={360} y={97} textAnchor="middle" fontSize="10" fill="#888">squaring = finding area</text>
          <text x={360} y={109} textAnchor="middle" fontSize="10" fill="#888">sqrt = finding side length</text>

          {/* Key points labels */}
          <rect x={330} y={120} width={180} height={76} rx={4} fill="#fff" stroke="#ccd9ea"/>
          <text x={420} y={136} textAnchor="middle" fontSize="11" fill="#1F3864" fontWeight="700">Perfect Squares</text>
          {[[1,1],[2,4],[3,9],[4,16],[5,25]].map(([n,sq],i) => (
            <g key={i}>
              <text x={340+i*34} y={154} textAnchor="middle" fontSize="10" fill="#2E75B6" fontFamily="'Courier New',monospace" fontWeight="700">{n}²</text>
              <text x={340+i*34} y={168} textAnchor="middle" fontSize="10" fill="#555" fontFamily="'Courier New',monospace">={sq}</text>
              <text x={340+i*34} y={188} textAnchor="middle" fontSize="9" fill="#c0392b" fontFamily="'Courier New',monospace">{"sqrt"+sq+"="+n}</text>
            </g>
          ))}
        </svg>
      </div>
      <div style={S.dCaption}>
        Squaring a number gives the area of a square with that side length. The square root reverses it — finding the side length from the area. The two functions are perfect mirror images of each other across y = x.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 4 — Absolute Value Function
// ─────────────────────────────────────────────────────────────────
function DiagramAbsoluteValue() {
  const W = 520, H = 260, ox = 220, oy = 200, sc = 44;
  const toX = x => ox + x * sc;
  const toY = y => oy - y * sc;

  const absPts = [];
  for (let x = -4.5; x <= 4.5; x += 0.05) {
    absPts.push(toX(x).toFixed(1) + ',' + toY(Math.abs(x)).toFixed(1));
  }

  // tolerance band example
  const target = 1, tol = 1.5;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Absolute Value — The V-Shape, Piecewise Definition, and Tolerance Bands</div>
      <div style={{ textAlign: 'center' }}>
        <svg width={W} height={H} viewBox={"0 0 " + W + " " + H} style={{ maxWidth: '100%' }}>
          {/* Grid */}
          {[-4,-3,-2,-1,0,1,2,3,4].map(v => <line key={v} x1={toX(v)} y1={10} x2={toX(v)} y2={oy+10} stroke="#eee" strokeWidth="1"/>)}
          {[0,1,2,3,4].map(v => <line key={v} x1={toX(-4.5)} y1={toY(v)} x2={toX(4.5)} y2={toY(v)} stroke="#eee" strokeWidth="1"/>)}
          {/* Axes */}
          <line x1={toX(-4.5)} y1={oy} x2={toX(4.5)} y2={oy} stroke="#1F3864" strokeWidth="1.5"/>
          <line x1={ox} y1={oy+10} x2={ox} y2={10} stroke="#1F3864" strokeWidth="1.5"/>
          <text x={toX(4.5)+4} y={oy+4} fontSize="11" fill="#1F3864" fontWeight="700">x</text>
          <text x={ox+4} y={12} fontSize="11" fill="#1F3864" fontWeight="700">y</text>
          {[-4,-3,-2,-1,1,2,3,4].map(v => <text key={v} x={toX(v)} y={oy+14} textAnchor="middle" fontSize="9" fill="#bbb">{v}</text>)}
          {[1,2,3,4].map(v => <text key={v} x={ox-5} y={toY(v)+3} textAnchor="end" fontSize="9" fill="#bbb">{v}</text>)}

          {/* Piecewise shading */}
          <path d={'M '+absPts.slice(0,Math.floor(absPts.length/2)+1).join(' L ')+" L "+toX(0)+","+oy+" L "+toX(-4.5)+","+oy+" Z"}
            fill="#ffd0d0" fillOpacity="0.25"/>
          <path d={'M '+absPts.slice(Math.floor(absPts.length/2)).join(' L ')+" L "+toX(4.5)+","+oy+" L "+toX(0)+","+oy+" Z"}
            fill="#d0e8ff" fillOpacity="0.25"/>

          {/* f(x) = |x| curve */}
          <path d={'M '+absPts.join(' L ')} fill="none" stroke="#2E75B6" strokeWidth="2.8"/>

          {/* Vertex */}
          <circle cx={ox} cy={oy} r={6} fill="#e07b00" stroke="#fff" strokeWidth="2"/>
          <text x={ox+8} y={oy-10} fontSize="11" fill="#e07b00" fontWeight="700">vertex (0, 0)</text>

          {/* Piecewise labels */}
          <text x={toX(-2.5)} y={toY(2)+4} fontSize="11" fill="#c0392b" fontWeight="700">-x  (when x &lt; 0)</text>
          <text x={toX(1.2)} y={toY(2)+4} fontSize="11" fill="#2E75B6" fontWeight="700">x  (when x &gt;= 0)</text>

          {/* Tolerance band */}
          <rect x={toX(target-tol)} y={toY(0.25)} width={toX(target+tol)-toX(target-tol)} height={toY(0)-toY(0.25)} fill="#27ae60" fillOpacity="0.15" stroke="#27ae60" strokeWidth="1.3" strokeDasharray="4,3"/>
          <line x1={toX(target)} y1={toY(0)} x2={toX(target)} y2={toY(4)} stroke="#27ae60" strokeWidth="1.5" strokeDasharray="3,3"/>
          <text x={toX(target)} y={toY(4.2)} textAnchor="middle" fontSize="10" fill="#27ae60" fontWeight="700">target = {target}</text>
          <text x={toX(target-tol)} y={oy+24} textAnchor="middle" fontSize="9" fill="#27ae60">{target-tol}</text>
          <text x={toX(target+tol)} y={oy+24} textAnchor="middle" fontSize="9" fill="#27ae60">{target+tol}</text>
          <text x={toX(target)} y={oy+36} textAnchor="middle" fontSize="10" fill="#27ae60" fontWeight="700">|x - {target}| &lt;= {tol}  (tolerance band)</text>

          {/* Formula panel */}
          <rect x={W-175} y={12} width={166} height={68} rx={4} fill="#fff" stroke="#ccd9ea"/>
          <text x={W-92} y={28} textAnchor="middle" fontSize="11" fill="#1F3864" fontWeight="700">Piecewise definition</text>
          <text x={W-160} y={44} fontSize="11" fill="#1F3864" fontFamily="'Courier New',monospace">|x| =</text>
          <text x={W-128} y={44} fontSize="11" fill="#2E75B6"  fontFamily="'Courier New',monospace">x   if x >= 0</text>
          <text x={W-128} y={60} fontSize="11" fill="#c0392b" fontFamily="'Courier New',monospace">-x  if x &lt; 0</text>
          <text x={W-92} y={76} textAnchor="middle" fontSize="9" fill="#888">always returns positive value</text>
        </svg>
      </div>
      <div style={S.dCaption}>
        The absolute value function creates a V-shape by reflecting the negative side upward. The expression |x - target| measures distance from target — perfect for writing tolerance, error, and deviation constraints in engineering specs.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 5 — Order of Operations (PEMDAS)
// ─────────────────────────────────────────────────────────────────
function DiagramOrderOfOps() {
  const W = 520, H = 260;

  const steps = [
    { abbr: 'P', full: 'Parentheses', desc: 'Evaluate innermost ( ) first', color: '#9b59b6', ex: '(3 + 2) * 4 = 5 * 4 = 20' },
    { abbr: 'E', full: 'Exponents', desc: 'Powers and roots next', color: '#2E75B6', ex: '2^3 + 1 = 8 + 1 = 9' },
    { abbr: 'MD', full: 'Multiply / Divide', desc: 'Left to right, equal priority', color: '#27ae60', ex: '12 / 4 * 3 = 3 * 3 = 9' },
    { abbr: 'AS', full: 'Add / Subtract', desc: 'Left to right, equal priority', color: '#e07b00', ex: '10 - 3 + 2 = 7 + 2 = 9' },
  ];

  const bW = 118, bH = 68, gap = 6, startX = 14, startY = 20;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Order of Operations — PEMDAS: Which Step Runs First</div>
      <div style={{ textAlign: 'center' }}>
        <svg width={W} height={H} viewBox={"0 0 " + W + " " + H} style={{ maxWidth: '100%' }}>
          <defs>
            <marker id="pemArr" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto"><polygon points="0 0,8 4,0 8" fill="#bbb"/></marker>
          </defs>

          {/* Step boxes */}
          {steps.map(({ abbr, full, desc, color, ex }, i) => {
            const bx = startX + i*(bW+gap);
            return (
              <g key={i}>
                {/* Priority label */}
                <text x={bx+bW/2} y={startY-6} textAnchor="middle" fontSize="10" fill="#aaa">step {i+1}</text>
                {/* Main box */}
                <rect x={bx} y={startY} width={bW} height={bH} rx={6} fill="#fff" stroke={color} strokeWidth="2.5"/>
                {/* Abbr badge */}
                <rect x={bx+bW/2-18} y={startY-1} width={36} height={22} rx={11} fill={color}/>
                <text x={bx+bW/2} y={startY+14} textAnchor="middle" fontSize="13" fill="#fff" fontWeight="700">{abbr}</text>
                <text x={bx+bW/2} y={startY+32} textAnchor="middle" fontSize="11" fill={color} fontWeight="700">{full}</text>
                <text x={bx+bW/2} y={startY+46} textAnchor="middle" fontSize="9"  fill="#666">{desc}</text>
                {/* Arrow to next */}
                {i < 3 && <line x1={bx+bW+4} y1={startY+bH/2} x2={bx+bW+gap-2} y2={startY+bH/2} stroke="#bbb" strokeWidth="2" markerEnd="url(#pemArr)"/>}
              </g>
            );
          })}

          {/* Examples panel */}
          <rect x={14} y={startY+bH+22} width={W-28} height={140} rx={5} fill="#fff" stroke="#ccd9ea"/>
          <text x={W/2} y={startY+bH+38} textAnchor="middle" fontSize="11" fill="#1F3864" fontWeight="700">Worked Example: 3 + 4^2 * (6 - 2) / 8</text>

          {[
            ['Step 1 — Parentheses:', '(6 - 2) = 4',           '3 + 4^2 * 4 / 8', '#9b59b6'],
            ['Step 2 — Exponents:',   '4^2 = 16',              '3 + 16 * 4 / 8',  '#2E75B6'],
            ['Step 3 — Multiply:',    '16 * 4 = 64',           '3 + 64 / 8',      '#27ae60'],
            ['Step 3 — Divide:',      '64 / 8 = 8',            '3 + 8',           '#27ae60'],
            ['Step 4 — Add:',         '3 + 8 = 11',            '= 11',            '#e07b00'],
          ].map(([step, action, result, color], i) => (
            <g key={i}>
              <text x={24}    y={startY+bH+56+i*20} fontSize="10" fill="#888"  fontFamily="'Courier New',monospace">{step}</text>
              <text x={188}   y={startY+bH+56+i*20} fontSize="10" fill={color} fontFamily="'Courier New',monospace" fontWeight="700">{action}</text>
              <text x={320}   y={startY+bH+56+i*20} fontSize="10" fill="#1F3864" fontFamily="'Courier New',monospace">→ {result}</text>
            </g>
          ))}

          {/* Common mistake box */}
          <rect x={14} y={startY+bH+155} width={W-28} height={22} rx={3} fill="#fff0f0" stroke="#c0392b" strokeWidth="1"/>
          <text x={W/2} y={startY+bH+170} textAnchor="middle" fontSize="10" fill="#c0392b" fontWeight="700">
            Common mistake: 3 + 4^2 * 2 is NOT (3+4)^2 * 2 = 98.  Exponents bind tighter than addition.  Answer is 3 + 16*2 = 35.
          </text>
        </svg>
      </div>
      <div style={S.dCaption}>
        PEMDAS is not arbitrary — it is a convention that makes expressions unambiguous. When in doubt, add parentheses to make your intent explicit. In code and calculator entry, extra parentheses never hurt.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 6 — ME Applications
// ─────────────────────────────────────────────────────────────────
function DiagramMEApplications() {
  const W = 520, H = 260;

  const apps = [
    { op: 'Addition', symbol: '+', color: '#2E75B6',
      cases: ['Forces in equilibrium: F_net = F1 + F2 + F3', 'Resistances in series: R_total = R1 + R2', 'Combined load: P_total = P_dead + P_live'] },
    { op: 'Subtraction', symbol: '-', color: '#c0392b',
      cases: ['Net force: F_net = F_applied - F_friction', 'Clearance fit: gap = hole_dia - shaft_dia', 'Pressure drop: dP = P_in - P_out'] },
    { op: 'Multiplication', symbol: 'x', color: '#27ae60',
      cases: ['Force x distance = moment (torque)', 'Pressure x area = force', 'Unit conversions: 1 ft x 12 in/ft = 12 in'] },
    { op: 'Division', symbol: '/', color: '#e07b00',
      cases: ['Safety factor: SF = F_ultimate / F_applied', 'Stress: sigma = F / A', 'Gear ratio: omega_out = omega_in / N'] },
    { op: 'Squaring', symbol: 'x²', color: '#9b59b6',
      cases: ['Kinetic energy: KE = (1/2)mv^2', 'Area: A = pi*r^2', 'RMS voltage: V_rms = sqrt(mean(V^2))'] },
    { op: 'Square Root', symbol: 'sqrt', color: '#1F3864',
      cases: ['Natural frequency: wn = sqrt(k/m)', 'RMS: V_rms = sqrt(mean of V^2)', 'Pythagorean: hyp = sqrt(a^2 + b^2)'] },
  ];

  const cols = 3, rows = 2;
  const cW = (W-28)/cols - 8, cH = (H-20)/rows - 8;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Integer Operations in Mechanical Engineering — One Example Per Operation</div>
      <div style={{ textAlign: 'center' }}>
        <svg width={W} height={H} viewBox={"0 0 " + W + " " + H} style={{ maxWidth: '100%' }}>
          {apps.map(({ op, symbol, color, cases }, i) => {
            const col = i % cols, row = Math.floor(i / cols);
            const bx = 14 + col*(cW+8), by = 10 + row*(cH+8);
            return (
              <g key={i}>
                <rect x={bx} y={by} width={cW} height={cH} rx={5} fill="#fff" stroke={color} strokeWidth="1.8"/>
                {/* Header */}
                <rect x={bx} y={by} width={cW} height={22} rx={5} fill={color} opacity="0.15"/>
                <rect x={bx} y={by+10} width={cW} height={12} fill={color} opacity="0.15"/>
                <text x={bx+8} y={by+14} fontSize="11" fill={color} fontWeight="700">{op}</text>
                <rect x={bx+cW-30} y={by+4} width={24} height={16} rx={8} fill={color}/>
                <text x={bx+cW-18} y={by+15} textAnchor="middle" fontSize="11" fill="#fff" fontWeight="700">{symbol}</text>
                {/* Cases */}
                {cases.map((c, ci) => (
                  <text key={ci} x={bx+8} y={by+34+ci*18} fontSize="9" fill="#444" fontFamily="'Courier New',monospace">{c}</text>
                ))}
              </g>
            );
          })}
        </svg>
      </div>
      <div style={S.dCaption}>
        Every integer operation appears constantly in ME. Multiplication and division are the workhorses of unit conversion and safety factors. Squaring and square roots appear in energy, frequency, and geometric calculations.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────
export default function IntegerFunctions(props) {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Integer Functions</h1>

      <p style={S.p}>
        Integer functions are the foundational operations of arithmetic — the building blocks every other area of mathematics is built on. In engineering these operations never go away; they appear in every formula, every unit conversion, every safety factor calculation, and every line of analysis code.
      </p>

      <InfoBox
        title="The Eight Basic Integer Operations"
        items={[
          "Addition (+) and Subtraction (-): combine or remove quantities",
          "Multiplication (*) and Division (/): scale quantities, repeated addition/splitting",
          "Inversion (1/x): the reciprocal — multiplying by 1/x is the same as dividing by x",
          "Squaring (x²) and Square Root (sqrt): area and its inverse, appear in energy and frequency",
          "Absolute Value (|x|): distance from zero, always non-negative",
        ]}
      />

      {/* Number Line */}
      <h2 style={S.h2}>The Number Line and Absolute Value</h2>
      <DiagramNumberLine />
      <Table2 rows={[
        ["Integers",           "Whole numbers and their negatives: ...-3, -2, -1, 0, 1, 2, 3...\nNo fractions or decimals"],
        ["Sign",               "Positive numbers lie right of zero.  Negative numbers lie left.\nSign tells direction, magnitude tells size"],
        ["Absolute value",     "|x| = distance from zero\n|x| = x if x >= 0,  |x| = -x if x < 0\nAlways returns a non-negative number"],
        ["Distance formula",   "|a - b| = distance between a and b on the number line\nOrder does not matter: |a-b| = |b-a|"],
        ["Double negative",    "-(-x) = x  — negating twice returns the original\nExample: -(-5) = 5"],
      ]} />

      {/* Four Operations */}
      <h2 style={S.h2}>The Four Basic Operations</h2>
      <DiagramOperations />
      <Table2 rows={[
        ["Addition",       "a + b = b + a  (commutative)\n(a+b)+c = a+(b+c)  (associative)\nIdentity: a + 0 = a"],
        ["Subtraction",    "a - b = a + (-b)  — subtracting is adding the negative\nNot commutative: a-b is not b-a in general"],
        ["Multiplication", "a * b = b * a  (commutative)\nDistributive: a*(b+c) = a*b + a*c\nIdentity: a * 1 = a.  Zero property: a * 0 = 0"],
        ["Division",       "a / b = a * (1/b)  — dividing is multiplying by the reciprocal\nUndefined when b = 0\nNot commutative: a/b is not b/a in general"],
        ["Inversion",      "1/x — the reciprocal of x\n(1/x) * x = 1  (inverse property)\nUsed constantly in unit conversions and formula rearrangement"],
      ]} />

      {/* Squaring and Square Root */}
      <h2 style={S.h2}>Squaring and Square Roots</h2>
      <DiagramSquareRoot />
      <Table2 rows={[
        ["Squaring",           "x^2 = x * x\nGeometric meaning: area of a square with side x\nAlways non-negative: x^2 >= 0 for all real x"],
        ["Square root",        "sqrt(x) = the non-negative number that squares to x\nsqrt(x)^2 = x  (for x >= 0)\nDomain: x >= 0  (no real square root of negative numbers)"],
        ["Inverse relationship","x^2 and sqrt(x) are inverses of each other\nThey mirror across y = x on a graph"],
        ["Perfect squares",    "1, 4, 9, 16, 25, 36, 49, 64, 81, 100...\nMemorise these — they appear constantly"],
        ["Negative under root", "sqrt(-1) = i (imaginary number)\nNo real solution — appears in underdamped vibration problems"],
        ["ME use",             "sqrt(k/m) = natural frequency\nsqrt(a^2+b^2) = magnitude of a 2D vector (Pythagorean theorem)\nsqrt(sum of squares) = RMS value of a signal"],
      ]} />

      {/* Absolute Value */}
      <h2 style={S.h2}>Absolute Value</h2>
      <DiagramAbsoluteValue />
      <Table2 rows={[
        ["Definition",         "|x| = x if x >= 0,  |x| = -x if x < 0\nAlways returns the magnitude, discarding the sign"],
        ["V-shape graph",      "Vertex at (0, 0).  Slopes of +1 and -1.\nEven function — symmetric about y-axis"],
        ["Absolute value equation","| x - a | = d  means x is exactly distance d from a\nTwo solutions: x = a+d  and  x = a-d"],
        ["Absolute value inequality","| x - a | <= d  means x is within distance d of a\nEquivalent to:  a-d <= x <= a+d"],
        ["Tolerance notation", "Dimension: 50 mm +/- 0.1 mm\nMeans: |x - 50| <= 0.1\nAcceptable range: 49.9 to 50.1 mm"],
        ["ME use",             "Manufacturing tolerances, error margins, deviation from setpoint\nSignal magnitude: |V| regardless of polarity\nDistance between sensor reading and target value"],
      ]} />

      {/* Order of Operations */}
      <h2 style={S.h2}>Order of Operations</h2>
      <DiagramOrderOfOps />
      <Table2 rows={[
        ["PEMDAS",            "Parentheses → Exponents → Multiply/Divide → Add/Subtract\nAlso written BODMAS (Brackets, Orders, Division/Multiplication, Addition/Subtraction)"],
        ["Left to right",     "Multiply and divide are equal priority — evaluate left to right\nSame rule for add and subtract\nExample: 12/4*3 = (12/4)*3 = 9,  NOT 12/(4*3) = 1"],
        ["Parentheses",       "Always evaluated first, innermost first\nUse them liberally to avoid ambiguity in engineering calculations"],
        ["Nested expressions","Work from inside out\n((2+3)*4)^2 = (5*4)^2 = 20^2 = 400"],
        ["Unary minus",       "-3^2 = -(3^2) = -9  NOT (-3)^2 = +9\nThe exponent binds before the negation — use parentheses when you mean (-3)^2"],
        ["In code/calculators","Always check how your tool handles implicit multiplication and precedence\nWhen in doubt, add parentheses — they are always free"],
      ]} />

      {/* ME Applications */}
      <h2 style={S.h2}>Mechanical Engineering Applications</h2>
      <DiagramMEApplications />

      <InfoBox
        title="Key Takeaway — These Operations Never Stop Appearing"
        items={[
          "Addition: summing forces, loads, resistances, dimensions — every equilibrium equation.",
          "Subtraction: net force, clearance, pressure drop, error from setpoint.",
          "Multiplication: unit conversions, moments (F x d), work (F x displacement), scaling.",
          "Division: safety factors (ultimate/applied), stress (F/A), efficiency (output/input).",
          "Squaring: kinetic energy (v^2), area (r^2), signal power, moment of inertia (r^2 dm).",
          "Square root: natural frequencies (sqrt(k/m)), RMS values, vector magnitudes, Pythagorean distances.",
          "Absolute value: tolerances, error bands, signal magnitude, distance between two values.",
        ]}
      />
    </div>
  );
}